import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  CareerOfferings,
  uploadStudentPhoto,
  OfferedSubject,
  SubjectOfferingOption,
  enrollInOffering,
  fetchCareerOfferings,
  PortalCareer,
  PortalModule,
  PortalSubject,
  SessionExpiredError,
  StudentPortalData,
  changeStudentPassword,
  fetchStudentPortal,
  forceChangeStudentPassword,
  getStudentSession,
  logoutStudent,
} from '../../config/studentApi';
import {
  BLOCK_COLORS,
  ChangePasswordForm,
  Empty,
  IconCalificaciones,
  IconContrasena,
  IconHorario,
  PortalHeader,
  PrintButton,
  WeekSchedule,
  scheduleSlots,
} from './portal/PortalShared';
import './EstudianteDashboard.css';

type SubjectState = 'aprobada' | 'reprobada' | 'en_curso' | 'pendiente';

const SUBJECT_STATE_LABEL: Record<SubjectState, string> = {
  aprobada: 'Aprobada',
  reprobada: 'Reprobada',
  en_curso: 'En curso',
  pendiente: 'Pendiente',
};

const SUBJECT_STATE_CLASS: Record<SubjectState, string> = {
  aprobada: 'aprobado',
  reprobada: 'reprobado',
  en_curso: 'en_curso',
  pendiente: 'pendiente',
};

function subjectState(subject: PortalSubject): SubjectState {
  if (subject.status === 'PASSED') return 'aprobada';
  if (subject.status === 'FAILED') return 'reprobada';
  const enrolled = subject.enrolled ?? Boolean(subject.course_label);
  return enrolled ? 'en_curso' : 'pendiente';
}

type ModuleTone = 'aprobado' | 'en_curso' | 'pendiente';

const TONE_LABEL: Record<ModuleTone, string> = {
  aprobado: 'Completo',
  en_curso: 'En curso',
  pendiente: 'Sin iniciar',
};

// El avance se mide por materias: en programas flexibles un módulo no se "reprueba" como bloque.
function moduleTone(module: PortalModule): ModuleTone {
  const states = module.subjects.map(subjectState);
  if (states.length && states.every(s => s === 'aprobada')) return 'aprobado';
  if (states.some(s => s !== 'pendiente')) return 'en_curso';
  return 'pendiente';
}

function gradeTone(subject: PortalSubject): 'pass' | 'fail' | 'empty' {
  if (subject.status === 'PASSED') return 'pass';
  if (subject.status === 'FAILED') return 'fail';
  return 'empty';
}

type SectionId = 'matricular' | 'horario' | 'calificaciones' | 'proyeccion' | 'contrasena';

const IconMatricular = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="12" y="8" width="34" height="46" rx="4" fill="#e2e8f0" />
    <rect x="12" y="8" width="34" height="10" rx="4" fill="#0ea5e9" />
    <rect x="18" y="24" width="5" height="4" rx="1" fill="#22c55e" />
    <rect x="26" y="24.5" width="14" height="3" rx="1.5" fill="#94a3b8" />
    <rect x="18" y="32" width="5" height="4" rx="1" fill="#22c55e" />
    <rect x="26" y="32.5" width="11" height="3" rx="1.5" fill="#94a3b8" />
    <rect x="18" y="40" width="5" height="4" rx="1" fill="#94a3b8" />
    <rect x="26" y="40.5" width="9" height="3" rx="1.5" fill="#94a3b8" />
    <circle cx="46" cy="46" r="11" fill="#22c55e" />
    <path d="M46 40v12M40 46h12" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const IconProyeccion = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="10" y="10" width="44" height="44" rx="6" fill="#e2e8f0" />
    <rect x="17" y="36" width="7" height="12" rx="1.5" fill="#22c55e" />
    <rect x="28" y="28" width="7" height="20" rx="1.5" fill="#0ea5e9" />
    <rect x="39" y="20" width="7" height="28" rx="1.5" fill="#a855f7" />
    <path d="M16 30l12-9 9 5 12-10" stroke="#f59e0b" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M28 6l14 5-14 5-14-5z" fill="#1e293b" />
    <path d="M42 11v6" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SECTIONS: { id: SectionId; label: string; icon: React.FC; perCareer: boolean }[] = [
  { id: 'matricular', label: 'Matricular', icon: IconMatricular, perCareer: true },
  { id: 'horario', label: 'Ver horario', icon: IconHorario, perCareer: true },
  { id: 'calificaciones', label: 'Calificaciones', icon: IconCalificaciones, perCareer: true },
  { id: 'proyeccion', label: 'Proyección académica', icon: IconProyeccion, perCareer: true },
  { id: 'contrasena', label: 'Cambiar contraseña', icon: IconContrasena, perCareer: false },
];

interface SectionProps {
  data: StudentPortalData;
  career: PortalCareer | null;
  careers: PortalCareer[];
  careerIndex: number;
  onSelectCareer: (index: number) => void;
  onDataChanged: () => void;
  mustChangePassword: boolean;
  onPasswordChanged: () => void;
}

function useProgress(career: PortalCareer | null) {
  return useMemo(() => {
    const modules = career?.modules ?? [];
    const allSubjects = modules.flatMap(m => m.subjects);
    const graded = allSubjects.filter(s => s.grade !== null);
    const average = graded.length
      ? graded.reduce((sum, s) => sum + (s.grade as number), 0) / graded.length
      : null;
    const approvedSubjects = allSubjects.filter(s => s.status === 'PASSED').length;
    return {
      average: average === null ? null : Math.round(average * 10) / 10,
      approvedSubjects,
      currentSubjects: allSubjects.filter(s => subjectState(s) === 'en_curso').length,
      failedSubjects: allSubjects.filter(s => subjectState(s) === 'reprobada').length,
      totalSubjects: allSubjects.length,
      progressPct: allSubjects.length ? Math.round((approvedSubjects / allSubjects.length) * 100) : 0,
    };
  }, [career]);
}

const OTHER_REASON: Record<string, string> = {
  not_offered: 'No se oferta en este momento',
  not_offered_for_retake: 'No se oferta en este momento para repetirla',
  offerings_full: 'Sin cupos disponibles',
  previous_module_incomplete: 'Debes completar el módulo anterior',
  prerequisite_not_passed: 'Debes aprobar el prerrequisito',
};

function otherReason(subject: OfferedSubject) {
  const base = OTHER_REASON[subject.reason] ?? 'No disponible';
  return subject.reason === 'prerequisite_not_passed' && subject.prerequisite
    ? `${base}: ${subject.prerequisite}`
    : base;
}

function seatsLabel(option: SubjectOfferingOption) {
  if (option.capacity === null) return 'Sin límite';
  return `${Math.max(option.capacity - option.enrolled_count, 0)} de ${option.capacity}`;
}

const Matricular: React.FC<SectionProps> = ({ career, onDataChanged }) => {
  const [offerings, setOfferings] = useState<CareerOfferings | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);
  const enrollmentId = career?.enrollment_id;

  const load = useCallback(async () => {
    if (!enrollmentId) return;
    try {
      setOfferings(await fetchCareerOfferings(enrollmentId));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las materias ofertadas.');
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load]);

  const enroll = async (subject: OfferedSubject, option: SubjectOfferingOption) => {
    if (!enrollmentId) return;
    const when = option.schedule_info ? ` (${option.schedule_info})` : '';
    if (!window.confirm(`¿Matricular ${subject.subject}${when}?`)) return;
    setBusyId(option.id);
    setMessage(null);
    try {
      const text = await enrollInOffering(enrollmentId, option.id);
      setMessage({ tone: 'ok', text });
      await load();
      onDataChanged();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo matricular la materia.' });
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <Empty>{error}</Empty>;
  if (!offerings) return <Empty>Cargando materias ofertadas…</Empty>;

  const available = offerings.subjects.filter(s => s.can_enroll);
  const enrolled = offerings.subjects.filter(s => s.status === 'IN_PROGRESS');
  const others = offerings.subjects.filter(s => !s.can_enroll && (s.status === 'NOT_OFFERED' || s.status === 'BLOCKED'));

  return (
    <div className="est-enroll">
      {message && <div className={`est-message est-message--${message.tone}`}>{message.text}</div>}

      <h2 className="est-enroll-title">Materias ofertadas ({available.length})</h2>
      {!available.length ? (
        <Empty>En este momento no hay materias ofertadas que puedas matricular.</Empty>
      ) : (
        <table className="est-table est-table--framed est-table--enroll">
          <thead>
            <tr>
              <th>Materia</th>
              <th>Módulo</th>
              <th>Horario</th>
              <th>Docente</th>
              <th className="est-center">Cupos</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {available.flatMap(subject => subject.offerings
              .filter(o => o.has_capacity && !o.already_in)
              .map(option => (
                <tr key={option.id}>
                  <td>{subject.subject}</td>
                  <td className="est-muted">{subject.module}</td>
                  <td className="est-muted">
                    {option.schedule_info || 'Por definir'}
                    {option.modality && <span className="est-enroll-sub">{option.modality}{option.classroom ? ` · ${option.classroom}` : ''}</span>}
                  </td>
                  <td className="est-muted">{option.teacher || 'Por asignar'}</td>
                  <td className="est-center est-muted">{seatsLabel(option)}</td>
                  <td className="est-center">
                    <button
                      type="button"
                      className="est-btn est-btn--small"
                      disabled={busyId !== null}
                      onClick={() => enroll(subject, option)}
                    >
                      {busyId === option.id ? 'Matriculando…' : 'Matricular'}
                    </button>
                  </td>
                </tr>
              )))}
          </tbody>
        </table>
      )}

      {enrolled.length > 0 && (
        <>
          <h2 className="est-enroll-title">Materias matriculadas ({enrolled.length})</h2>
          <div className="est-chips">
            {enrolled.map(s => (
              <span key={s.subject_id} className="est-chip is-passed">{s.subject}</span>
            ))}
          </div>
        </>
      )}

      {others.length > 0 && (
        <details className="est-enroll-others">
          <summary>Otras materias de tu plan ({others.length})</summary>
          <table className="est-table">
            <tbody>
              {others.map(s => (
                <tr key={s.subject_id}>
                  <td>{s.subject}</td>
                  <td className="est-muted">{s.module}</td>
                  <td className="est-muted">{otherReason(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
};

const Horario: React.FC<SectionProps> = ({ data, careers, careerIndex, onSelectCareer }) => {
  const career = careers[careerIndex] ?? null;
  const items = useMemo(() => (career?.schedule ?? []).map((item, i) => ({
    ...item,
    color: BLOCK_COLORS[i % BLOCK_COLORS.length],
    slots: scheduleSlots(item.schedule_slots, item.schedule_info),
  })), [career]);
  const blocks = useMemo(() => items.map(item => ({
    key: `${item.module}-${item.subject}`,
    title: item.subject,
    detail: item.classroom,
    color: item.color,
    slots: item.slots,
  })), [items]);

  return (
    <div className="est-schedule">
      <div className="est-schedule-main">
        <div className="est-grades-bar est-schedule-bar">
          <span>Ver horario</span>
          <PrintButton />
        </div>

        <div className="est-schedule-info">
          <div className="est-schedule-student">
            <span>Nombre: {data.student.full_name}</span>
            <span>Código: {data.student.student_code ?? '—'} / Identificación: {data.student.document_number}</span>
          </div>
          <label className="est-filter">
            <span>Programa:</span>
            <select
              value={careerIndex}
              onChange={e => onSelectCareer(Number(e.target.value))}
              disabled={careers.length < 2}
            >
              {careers.map((c, i) => (
                <option key={c.enrollment_id} value={i}>{c.career.name}</option>
              ))}
            </select>
          </label>
        </div>

        <WeekSchedule blocks={blocks} />
      </div>

      <aside className="est-schedule-side">
        <div className="est-grades-bar">Descripción del horario</div>
        {!items.length ? (
          <p className="est-schedule-empty">No tienes materias matriculadas en este momento.</p>
        ) : (
          <ul className="est-schedule-list">
            {items.map(item => (
              <li key={`${item.module}-${item.subject}`}>
                <span className="est-schedule-dot" style={{ background: item.color }} />
                <div>
                  <strong>{item.subject}</strong>
                  <span>{item.schedule_info || 'Horario por definir'}</span>
                  <span>
                    {item.teacher || 'Docente por asignar'}
                    {' · '}
                    {item.classroom || 'Salón por asignar'}
                    {item.modality ? ` · ${item.modality}` : ''}
                  </span>
                  {item.schedule_info && !item.slots.length && (
                    <span className="est-schedule-warn">No se pudo ubicar en la cuadrícula</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
};

const Calificaciones: React.FC<SectionProps> = ({ data, careers, careerIndex, onSelectCareer }) => {
  const career = careers[careerIndex] ?? null;
  const modules = useMemo(() => career?.modules ?? [], [career]);
  const defaultModuleId = useMemo(() => {
    const current = modules.find(m => m.name === career?.current_module)
      ?? modules.find(m => moduleTone(m) === 'en_curso')
      ?? modules[0];
    return current?.id ?? null;
  }, [career, modules]);
  const [moduleId, setModuleId] = useState<number | null>(defaultModuleId);
  const module = modules.find(m => m.id === moduleId) ?? modules.find(m => m.id === defaultModuleId) ?? null;

  const graded = module?.subjects.filter(s => s.grade !== null) ?? [];
  const average = graded.length
    ? Math.round((graded.reduce((sum, s) => sum + (s.grade as number), 0) / graded.length) * 10) / 10
    : null;

  return (
    <div className="est-grades">
      <div className="est-grades-bar">
        ({data.student.student_code ?? '—'}) {data.student.full_name}
      </div>

      <div className="est-grades-filters">
        <label className="est-filter">
          <span>Programa:</span>
          <select
            value={careerIndex}
            onChange={e => { onSelectCareer(Number(e.target.value)); setModuleId(null); }}
            disabled={careers.length < 2}
          >
            {careers.map((c, i) => (
              <option key={c.enrollment_id} value={i}>{c.career.name}</option>
            ))}
          </select>
        </label>
        <label className="est-filter">
          <span>Módulo:</span>
          <select
            value={module?.id ?? ''}
            onChange={e => setModuleId(Number(e.target.value))}
            disabled={!modules.length}
          >
            {modules.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>
      </div>

      {!module ? (
        <Empty>Todavía no hay calificaciones registradas.</Empty>
      ) : (
        <table className="est-table est-table--framed est-table--grades">
          <thead>
            <tr>
              <th className="est-center">Grupo</th>
              <th>Curso</th>
              <th>Docente</th>
              <th className="est-center">Acum.</th>
              <th className="est-center">Def</th>
              <th className="est-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {module.subjects.map(s => (
              <tr key={s.subject}>
                <td className="est-center est-muted">{s.course_label ?? '—'}</td>
                <td>
                  {s.subject}
                  {!!s.components?.length && (
                    <span className="est-cortes">
                      {s.components.map(c => (
                        <span key={c.name} className={`est-corte${c.value === null ? ' is-empty' : ''}`}>
                          {c.name} ({c.weight}%): <strong>{c.value ?? '—'}</strong>
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td className="est-muted">{s.teacher || 'Por asignar'}</td>
                <td className="est-center est-muted">{s.partial ?? '—'}</td>
                <td className={`est-center est-grade est-grade--${gradeTone(s)}`}>
                  {s.grade === null ? '—' : s.grade}
                </td>
                <td className="est-center">
                  <span className={`est-status est-status--${SUBJECT_STATE_CLASS[subjectState(s)]}`}>
                    {SUBJECT_STATE_LABEL[subjectState(s)]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="est-grades-avg-label">
                Promedio del módulo (se aprueba con {career?.passing_grade} de {career?.max_grade}):
              </td>
              <td className="est-center est-grades-avg">{average ?? '—'}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

const Proyeccion: React.FC<SectionProps> = ({ career }) => {
  const progress = useProgress(career);

  if (!career || !career.modules.length) {
    return <Empty>Tu plan de estudios todavía no está disponible.</Empty>;
  }

  return (
    <>
      <section className="est-stats">
        <div className="est-stat">
          <span className="est-stat-value">{progress.approvedSubjects} / {progress.totalSubjects}</span>
          <span className="est-stat-label">Asignaturas aprobadas</span>
        </div>
        <div className="est-stat">
          <span className="est-stat-value">{progress.currentSubjects}</span>
          <span className="est-stat-label">Asignaturas en curso</span>
        </div>
        <div className="est-stat est-stat--wide">
          <div className="est-progress-head">
            <span className="est-stat-label">Avance del programa</span>
            <span className="est-progress-pct">{progress.progressPct}%</span>
          </div>
          <div className="est-progress-track">
            <div className="est-progress-fill" style={{ width: `${progress.progressPct}%` }} />
          </div>
        </div>
      </section>

      <div className="est-legend">
        {(['aprobada', 'en_curso', 'reprobada', 'pendiente'] as SubjectState[]).map(state => (
          <span key={state} className={`est-legend-item est-chip--${state}`}>{SUBJECT_STATE_LABEL[state]}</span>
        ))}
      </div>

      <ol className="est-timeline">
        {career.modules.map(module => {
          const tone = moduleTone(module);
          const passed = module.subjects.filter(s => s.status === 'PASSED').length;
          return (
            <li key={module.id} className={`est-timeline-item est-timeline-item--${tone}`}>
              <div className="est-timeline-head">
                <span className="est-module-name">{module.name}</span>
                <span className="est-module-count">{passed} de {module.subjects.length} aprobadas</span>
                {tone !== 'pendiente' && (
                  <span className={`est-status est-status--${tone}`}>{TONE_LABEL[tone]}</span>
                )}
              </div>
              <div className="est-chips">
                {module.subjects.map(s => {
                  const state = subjectState(s);
                  return (
                    <span
                      key={s.subject}
                      className={`est-chip est-chip--${state}`}
                      title={SUBJECT_STATE_LABEL[state] + (s.grade !== null ? ` · Nota ${s.grade}` : '')}
                    >
                      {s.subject}
                      {s.grade !== null && <strong className="est-chip-grade">{s.grade}</strong>}
                    </span>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
};

const CambiarContrasena: React.FC<SectionProps> = ({ mustChangePassword, onPasswordChanged }) => (
  <ChangePasswordForm
    mustChangePassword={mustChangePassword}
    onPasswordChanged={onPasswordChanged}
    changePassword={changeStudentPassword}
    forceChangePassword={forceChangeStudentPassword}
  />
);

const SECTION_CONTENT: Record<SectionId, React.FC<SectionProps>> = {
  matricular: Matricular,
  horario: Horario,
  calificaciones: Calificaciones,
  proyeccion: Proyeccion,
  contrasena: CambiarContrasena,
};

const EstudianteDashboard: React.FC = () => {
  const { seccion } = useParams<{ seccion?: string }>();
  const navigate = useNavigate();
  const session = getStudentSession();
  const [data, setData] = useState<StudentPortalData | null>(null);
  const [error, setError] = useState('');
  const [careerIndex, setCareerIndex] = useState(0);
  const [mustChangePassword, setMustChangePassword] = useState(Boolean(session?.mustChangePassword));
  const [welcome, setWelcome] = useState('');

  useEffect(() => {
    if (seccion) setWelcome('');
  }, [seccion]);

  const handlePasswordChanged = () => {
    if (!mustChangePassword) return;
    setMustChangePassword(false);
    setWelcome('Tu contraseña se actualizó correctamente. ¡Bienvenido(a) a tu portal!');
    navigate('/portal-estudiantil/inicio', { replace: true });
  };

  const logout = useCallback(() => {
    logoutStudent();
    navigate('/portal-estudiantil', { replace: true });
  }, [navigate]);

  const loadData = useCallback(async (isCancelled: () => boolean = () => false) => {
    if (!getStudentSession()) return;
    try {
      const result = await fetchStudentPortal();
      if (!isCancelled()) setData(result);
    } catch (err) {
      if (isCancelled()) return;
      if (err instanceof SessionExpiredError) {
        navigate('/portal-estudiantil', { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudieron cargar tus datos.');
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    loadData(() => cancelled);
    return () => { cancelled = true; };
  }, [loadData]);

  if (!session) {
    return <Navigate to="/portal-estudiantil" replace />;
  }

  const active = SECTIONS.find(s => s.id === seccion);
  if (seccion && !active) {
    return <Navigate to="/portal-estudiantil/inicio" replace />;
  }
  if (mustChangePassword && seccion !== 'contrasena') {
    return <Navigate to="/portal-estudiantil/inicio/contrasena" replace />;
  }

  const Content = active ? SECTION_CONTENT[active.id] : null;
  const careers = data?.careers ?? [];
  const career = careers[careerIndex] ?? careers[0] ?? null;

  return (
    <>
    <PortalHeader
      homeTo="/portal-estudiantil/inicio"
      name={data?.student.full_name ?? null}
      photo={data?.student.photo}
      meta={[
        `Usuario: ${data?.student.username ?? '—'}`,
        `Código: ${data?.student.student_code ?? '—'}`,
      ]}
      ready={Boolean(data)}
      onUploadPhoto={uploadStudentPhoto}
      onPhotoChanged={photo => setData(prev => (prev ? { ...prev, student: { ...prev.student, photo } } : prev))}
      onLogout={logout}
    />

    <div className="est-page">
      <div className="est-shell">
        <div className="est-titlebar">
          <span>Portal de Estudiantes</span>
        </div>

        {error && <div className="est-section"><Empty>{error}</Empty></div>}

        {!error && !data && <div className="est-section"><Empty>Cargando tu información…</Empty></div>}

        {data && !active && welcome && (
          <div className="est-section">
            <div className="est-message est-message--ok">{welcome}</div>
          </div>
        )}

        {data && !active && (
          <nav className="est-menu" aria-label="Opciones del portal">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <Link key={id} to={`/portal-estudiantil/inicio/${id}`} className="est-tile">
                <span className="est-tile-icon"><Icon /></span>
                <span className="est-tile-label">{label}</span>
              </Link>
            ))}
          </nav>
        )}

        {data && active && Content && (
          <section className="est-section">
            {!mustChangePassword && (
              <Link to="/portal-estudiantil/inicio" className="est-back">← Volver al menú</Link>
            )}
            <div className="est-section-head">
              <span className="est-section-icon"><active.icon /></span>
              <h1 className="est-section-title">{active.label}</h1>
              {active.perCareer && active.id !== 'calificaciones' && active.id !== 'horario' && careers.length > 1 && (
                <label className="est-career-select">
                  <span>Programa</span>
                  <select value={careerIndex} onChange={e => setCareerIndex(Number(e.target.value))}>
                    {careers.map((c, i) => (
                      <option key={c.enrollment_id} value={i}>{c.career.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {active.perCareer && !careers.length ? (
              <Empty>Todavía no estás inscrito en ningún programa.</Empty>
            ) : (
              <Content
                key={career?.enrollment_id ?? 'none'}
                data={data}
                career={career}
                careers={careers}
                careerIndex={careers[careerIndex] ? careerIndex : 0}
                onSelectCareer={setCareerIndex}
                onDataChanged={() => loadData()}
                mustChangePassword={mustChangePassword}
                onPasswordChanged={handlePasswordChanged}
              />
            )}
          </section>
        )}
      </div>
    </div>
    </>
  );
};

export default EstudianteDashboard;
